import React, { useEffect } from 'react';

import { Alert, Spacer } from '@zapier/design-system';
import { Spacing } from '@zapier/theme';

import ActionInput from 'app/developer-v3/pages/actions/ActionInput';
import EditorBodyLayout from 'app/developer-v3/layouts/EditorBodyLayout';
import Link from 'app/common/components/Link';
import { BASE_DOC_URL_VB } from 'app/developer-v3/constants';
import { getActionUrl } from 'app/developer-v3/utils';
import { useCurrentVersionReadOnlyReason } from 'app/developer-v3/hooks';

import type { ErrorList } from 'app/developer-v3/types/validator';
import type { StaticDropdown } from 'app/developer-v3/platformSchema/field';
import type { TriggerSchema } from 'app/developer-v3/platformSchema/trigger';
import type {
  ActionParams,
  FormifiedFieldSchema,
  ActionSchema,
  DropdownType,
  FormFieldTemplate,
} from 'app/developer-v3/types';

type Props = {
  action: ActionSchema;
  attemptedSave: boolean;
  errors: ErrorList;
  cancelRoute?: string;
  formifiedField: FormifiedFieldSchema;
  formFields: FormFieldTemplate[];
  isFormValid: boolean;
  isSaveDisabled: () => boolean;
  isSaving: boolean;
  onFormChange: (form: FormifiedFieldSchema) => void;
  params: ActionParams & { inputKey: string };
  searches: ActionSchema[];
  resetUnsavedEdits: () => void;
  saveActionChanges: () => void;
  setInputField: (inputKey: string) => void;
  setDropdown: (type: DropdownType, dropdown: string | StaticDropdown) => void;
  setSearch: (type: DropdownType, searchRef: string) => void;
  toggleDropdown: (isDropdownEnabled: boolean) => void;
  toggleSearch: (isDropdownEnabled: boolean) => void;
  availableTriggers: TriggerSchema[];
};

const ActionInputEditor = (props: Props) => {
  const {
    cancelRoute,
    isSaveDisabled,
    isSaving,
    saveActionChanges,
    ...restProps
  } = props;
  const {
    formifiedField,
    params,
    onFormChange,
    resetUnsavedEdits,
    setInputField,
  } = restProps;

  const { inputKey } = params;

  const isReadOnly = !!useCurrentVersionReadOnlyReason();

  useEffect(() => {
    setInputField(inputKey);
    return () => resetUnsavedEdits();
  }, [inputKey, resetUnsavedEdits, setInputField]);

  return (
    <EditorBodyLayout
      cancelRoute={cancelRoute || `${getActionUrl(params)}/input`}
      isDisabled={isSaveDisabled() || isReadOnly}
      isSaving={isSaving}
      onSave={saveActionChanges}
    >
      <Alert status="info">
        <Link
          href={`${BASE_DOC_URL_VB}/publish/integration-publishing-requirements#5-7-integration-uses-english-language`}
          target="_blank"
        >
          Use English
        </Link>{' '}
        for all user-facing text.{' '}
        <Link
          href={`${BASE_DOC_URL_VB}/publish/integration-build-guidelines#action-input-fields`}
          target="_blank"
        >
          Learn more
        </Link>{' '}
        about action input fields.
      </Alert>
      <Spacer height={Spacing.medium} />
      <ActionInput
        {...restProps}
        onBlur={({ field }) => {
          onFormChange({ ...formifiedField, [field.key]: field.value.trim() });
        }}
      />
    </EditorBodyLayout>
  );
};

export default ActionInputEditor;
